import { useEffect, useRef, useState } from 'react'

import type { Remark } from '../../api/exams'
import { rendererFor as lookUp, type RendererFor } from '../../ui/preview/renderers'
import {
  bringIntoView,
  markPassages,
  MARK_CLASS,
  NOTE_CLASS,
  type Note,
  type Passage,
} from './marking'

/** Where the notes stand. Remembered in this browser: it is a habit, not a fact about the paper. */
export type NotesAt = 'paragraph' | 'margin'

/** The least room between two notes stacked in the margin. */
const GAP = 8

/**
 * What somebody wrote, drawn, with the correction written **into** it.
 *
 * **The note is at the text and not beside it.** The first attempt marked the passage here and left
 * the words of the point on the other half of the screen, which is still reading in two places —
 * exactly the complaint it was meant to answer. The note itself now stands in the answer, at the end
 * of the paragraph its passage is in, and a **number** on the mark and on the note is what ties them
 * together. The paragraph somebody wrote stays one paragraph; the number is counted in the reading
 * order of the text, because it is a property of the answer and not a reference into the judgement.
 *
 * **Two arrangements, and the reader picks.** At the end of the paragraph, or out in a margin beside
 * the line the passage is on. The second is what a marked-up essay looks like, and it needs room: on
 * a narrow screen there is no margin, so there is no switch either and the notes stand at the
 * paragraph.
 *
 * The marking happens in the *drawn* text and not in the source: the drawn text is what is being
 * read, and a quote out of somebody's answer never carries the markup around it. Everything about
 * how a passage is found, how two that overlap are cut apart, and how they are numbered lives in
 * `marking.ts`.
 *
 * **The marks are put in before the text reaches the page, and never afterwards.** Marking drawn DOM
 * would mean changing what React owns, and the next render puts back what React last wrote — the
 * marks would come and go. So this asks the renderer itself, marks a detached copy, and hands the
 * finished markup over once. That is also why it does not use `ui/preview/Preview`: the same
 * renderers, the same guarantee about what they emit, one step earlier.
 */
export interface MarkedAnswerProps {
  readonly text: string
  /** The markup the answer is written in, as a media type. */
  readonly format: string
  readonly label: string
  /** Every point of the judgement, in the order it was given. The index is the point's name here. */
  readonly remarks: readonly Remark[]
  /** Which points belong to the task on the screen — the others are about another answer. */
  readonly taskId: string
  /** Where the notes stand. */
  readonly notesAt: NotesAt
  /** The point being looked at, by index, or `null`. */
  readonly selected: number | null
  readonly onSelect: (index: number | null) => void
  /** Said after every drawing: which of this task's points could not be found in the text. */
  readonly onUnfound: (indexes: readonly number[]) => void
  /** For a test, or for the day a renderer comes from somewhere else. */
  readonly rendererFor?: RendererFor
}

export function MarkedAnswer({
  text,
  format,
  label,
  remarks,
  taskId,
  notesAt,
  selected,
  onSelect,
  onUnfound,
  rendererFor,
}: MarkedAnswerProps) {
  const lookingUp = rendererFor ?? lookUp
  const [drawn, setDrawn] = useState<string | null>(null)
  const [nothingDraws, setNothingDraws] = useState(false)
  const body = useRef<HTMLDivElement>(null)
  // The box a note in the margin is placed against, and the one that scrolls. Both are this element
  // and not `.preview__body`: absolute positioning goes by the padding box of the nearest positioned
  // ancestor, and measuring against the inner box would put every note one padding out.
  const host = useRef<HTMLDivElement>(null)

  // Kept in a ref for the reason every callback prop here is: a caller handing in a fresh function
  // on every render must not make this draw again, and drawing is the expensive thing.
  const report = useRef(onUnfound)
  useEffect(() => {
    report.current = onUnfound
  }, [onUnfound])

  // Said as strings rather than as the arrays themselves: a fresh array on every render would be a
  // fresh dependency on every render, and this effect draws.
  const passagesSaid = JSON.stringify(
    remarks.flatMap((remark, index): Passage[] =>
      remark.taskId === taskId && remark.quote !== null ? [{ index, quote: remark.quote }] : [],
    ),
  )
  const notesSaid = JSON.stringify(remarks.map((one): Note => ({ tone: one.tone, text: one.text })))

  useEffect(() => {
    let current = true
    void (async () => {
      const renderer = await lookingUp(format)
      if (!current) {
        return
      }
      if (renderer === null) {
        setNothingDraws(true)
        return
      }
      const rendering = await renderer.render(text)
      if (!current) {
        return
      }
      if (rendering.kind !== 'html') {
        // An answer is always markup. A renderer that draws a file has nothing to mark.
        setNothingDraws(true)
        return
      }
      setNothingDraws(false)

      const detached = document.createElement('div')
      detached.innerHTML = rendering.html
      const notes = JSON.parse(notesSaid) as Note[]
      const marked = markPassages(
        detached,
        JSON.parse(passagesSaid) as Passage[],
        (index) => notes[index],
      )
      setDrawn(detached.innerHTML)
      report.current(marked.unfound)
    })()
    return () => {
      current = false
    }
  }, [text, format, lookingUp, passagesSaid, notesSaid])

  // The mark and the note being looked at, told apart from the rest, and the passage brought onto
  // the screen. The note is marked too: in the margin it is the thing the eye is going to.
  useEffect(() => {
    const element = body.current
    if (element === null) {
      return
    }
    let wanted: HTMLElement | null = null
    element.querySelectorAll<HTMLElement>(`.${MARK_CLASS}`).forEach((one) => {
      const isIt = (one.dataset.notes ?? '').split(',').includes(String(selected))
      one.dataset.at = isIt ? 'true' : 'false'
      if (isIt && wanted === null) {
        wanted = one
      }
    })
    element.querySelectorAll<HTMLElement>(`.${NOTE_CLASS}`).forEach((one) => {
      one.dataset.at = one.dataset.note === String(selected) ? 'true' : 'false'
    })
    if (wanted !== null) {
      bringIntoView(wanted)
    }
  }, [selected, drawn, notesAt])

  // Out in the margin, a note stands at the height of its own passage. That is a measurement and
  // can only happen once the text has been laid out, which is why it is done here and not while the
  // markup is being built. They are stacked where two would otherwise sit on top of each other.
  useEffect(() => {
    const element = body.current
    if (element === null) {
      return
    }
    const notes = [...element.querySelectorAll<HTMLElement>(`.${NOTE_CLASS}`)]
    if (notesAt !== 'margin') {
      notes.forEach((one) => {
        one.style.removeProperty('top')
      })
      return
    }
    const marks = [...element.querySelectorAll<HTMLElement>(`.${MARK_CLASS}`)]
    const around = host.current
    if (around === null) {
      return
    }
    // The origin of the containing block: the padding box, in the coordinates the notes are written
    // in — which scroll, so what has already been scrolled has to be added back.
    const origin = around.getBoundingClientRect().top + around.clientTop - around.scrollTop
    let lowest = 0
    notes.forEach((one) => {
      const named = one.dataset.note ?? ''
      const mark = marks.find((it) => (it.dataset.notes ?? '').split(',').includes(named))
      const at = mark === undefined ? lowest : mark.getBoundingClientRect().top - origin
      // Never above the one before it: two passages on neighbouring lines would otherwise put two
      // notes on top of each other.
      const put = Math.max(at, lowest)
      one.style.top = `${String(put)}px`
      lowest = put + one.getBoundingClientRect().height + GAP
    })
  }, [notesAt, drawn, selected])

  if (nothingDraws) {
    return (
      <div className="marked-answer preview preview--none" role="region" aria-label={label}>
        <p className="preview__note">
          MyLib cannot draw this kind of text. It is kept exactly as it was written.
        </p>
      </div>
    )
  }

  return (
    <div
      className="marked-answer preview"
      role="region"
      aria-label={label}
      data-notes-at={notesAt}
      ref={host}
    >
      {/*
        Put into the page as markup, which is what drawing markup means. Safe for the same reason
        `ui/preview/Preview` is: each renderer answers for its own output — Markdown with raw HTML
        switched off at the parser, AsciiDoc sanitised. The marks and the notes added above are
        elements this file made itself, out of text that is set with `textContent`.
      */}
      <div
        className="preview__body"
        ref={body}
        onClick={(event) => {
          const at = (event.target as Element | null)?.closest?.(
            `.${MARK_CLASS}, .${NOTE_CLASS}`,
          ) as HTMLElement | null
          if (at === null) {
            return
          }
          const named = at.dataset.note ?? at.dataset.notes?.split(',')[0]
          if (named !== undefined) {
            onSelect(Number(named))
          }
        }}
        dangerouslySetInnerHTML={{ __html: drawn ?? '' }}
      />
    </div>
  )
}
